import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Text component variants using class-variance-authority (cva)
 *
 * This configuration maps the legacy Text component's styled-components
 * logic to Tailwind CSS utility classes with type-safe variants.
 */
export const textVariants = cva(
  // Base classes applied to all Text instances
  'inline-block', // Default to inline-block for better layout control
  {
    variants: {
      /**
       * Size variants - maps to Tailwind font-size utilities
       * Corresponds to theme.FONT_SIZE.* values from legacy implementation
       */
      size: {
        XXSmall: 'text-xxs', // 10px - theme.FONT_SIZE.XXS (uses design system token)
        xSmall: 'text-xs', // 12px - theme.FONT_SIZE.XS
        small: 'text-sm', // 14px - theme.FONT_SIZE.SM
        medium: 'text-base', // 16px - theme.FONT_SIZE.MD
        large: 'text-lg', // 18px - theme.FONT_SIZE.LG
        xLarge: 'text-xl', // 20px - theme.FONT_SIZE.XL
        XXLarge: 'text-2xl', // 24px - theme.FONT_SIZE.XXL
      },

      /**
       * Font weight variants
       * Note: bold prop in TextProps overrides this
       */
      weight: {
        normal: 'font-normal', // 400
        bold: 'font-bold', // 700
      },

      /**
       * Text alignment variants
       */
      align: {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right',
        justify: 'text-justify',
      },

      /**
       * Truncation variant
       * Applies ellipsis overflow handling
       */
      truncate: {
        true: 'truncate overflow-hidden whitespace-nowrap',
        false: '',
      },

      /**
       * Underline variant
       */
      underline: {
        true: 'underline',
        false: '',
      },

      /**
       * Clickable variant
       * Adds pointer cursor when onClick is present
       */
      clickable: {
        true: 'cursor-pointer',
        false: 'cursor-default',
      },

      /**
       * Font family variant
       * Uses secondary font when enabled
       */
      fontFamily: {
        primary: '', // Default font family from Tailwind config
        secondary: 'font-secondary', // Custom font-secondary class
      },
    },

    defaultVariants: {
      size: 'medium',
      weight: 'normal',
      align: 'left',
      truncate: false,
      underline: false,
      clickable: false,
      fontFamily: 'primary',
    },
  }
);

/**
 * Color preset mappings to Tailwind CSS classes
 *
 * Maps legacy theme.COLORS.* values to Tailwind/shadcn color system
 */
export const colorClassMap: Record<string, string> = {
  white: 'text-white',
  black: 'text-foreground',
  gray: 'text-muted-foreground',
  green: 'text-success',
  red: 'text-destructive',
  yellow: 'text-warning',
  primary: 'text-primary',
  textPrimary: 'text-foreground',
  textSecondary: 'text-muted-foreground',
};

/**
 * Helper function to resolve color to either a Tailwind class or custom CSS color
 *
 * @param color - Color preset name or custom CSS color value
 * @returns Object with either className or style property
 */
export const resolveColor = (color?: string): { className?: string; style?: { color: string } } => {
  if (!color) return {};

  // Check if it's a preset color
  if (color in colorClassMap) {
    return { className: colorClassMap[color] };
  }

  // Custom color (CSS variable, hex, rgb, etc.)
  return { style: { color } };
};

/**
 * Type-safe variant props extracted from cva configuration
 */
export type TextVariants = VariantProps<typeof textVariants>;
